import { TranslocoModule, provideTranslocoScope, translate } from '@jsverse/transloco';
import { Component, computed, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { HeaderService } from 'core';
import { CartService } from 'products';
import { finalize, switchMap } from 'rxjs';
import { OrderProposal } from '../../interfaces/order-proposal.model';
import { OrderProposalService } from '../../services/order-proposal.service';
import { isDraftProposal, proposalNeedsCodeChoice } from '../../utils/order.utils';
import { NewCustomerRequestBanner } from '../new-customer/new-customer-request-banner/new-customer-request-banner';
import { ProposalAnomalies } from '../proposal-anomalies/proposal-anomalies';
import { DraftSavedDialog } from './draft-saved-dialog/draft-saved-dialog';
import { SaveDraftDialog, SaveDraftDialogResult } from './save-draft-dialog/save-draft-dialog';

@Component({
  providers: [provideTranslocoScope('orders')],
  selector: 'ord-new-order-proposal',
  imports: [DatePipe, MatButtonModule, TranslocoModule, NewCustomerRequestBanner, ProposalAnomalies],
  templateUrl: './new-order-proposal.html',
  styleUrl: './new-order-proposal.scss',
})
export class NewOrderProposal implements OnInit, OnDestroy {
  private readonly headerService = inject(HeaderService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly dialog = inject(MatDialog);
  private readonly cartService = inject(CartService);
  private readonly proposalService = inject(OrderProposalService);

  readonly proposal = this.proposalService.proposal;

  // Finché la proposta richiesta non è aperta, in memoria può esserci la precedente
  readonly ready = signal(false);

  // Righe attive del carrello della proposta, escluse quelle archiviate
  readonly productsCount = computed(
    () => (this.cartService.cart()?.righe ?? []).filter(row => !row.archiviata).length,
  );

  readonly customer = computed(() => {
    const proposal = this.proposal();
    return proposal?.idBusinessPartner
      ? {
          idBusinessPartner: proposal.idBusinessPartner,
          ragioneSociale: proposal.ragioneSociale ?? '',
          codes: [proposal.oil.codiceCliente, proposal.nonOil.codiceCliente].filter(code => code != null).join(' - '),
        }
      : null;
  });
  readonly customerSelected = computed(() => this.customer() !== null);
  readonly productsSelected = computed(() => this.productsCount() > 0);

  // La «X» c'è solo finché la proposta non è mai stata salvata: la bozza si salva da sola
  private readonly closeAction = effect(() => {
    if (this.ready() && this.proposal()?.stato === 'Nuovo') {
      this.headerService.setActions([{ get label() { return translate<string>('orders.ui.chiudi'); }, icon: 'fa-xmark', action: () => this.close() }]);
    } else {
      this.headerService.removeActions();
    }
  });

  ngOnInit(): void {
    this.headerService.setTitle('orders.ui.nuovaPropostaDOrdine');

    // Senza id nasce una proposta: dal catalogo col carrello, dal dettaglio cliente col cliente, altrimenti vuota
    const params = this.route.snapshot.queryParamMap;
    const id = params.get('id');
    const open$ = id
      ? this.proposalService.ensure(id)
      : this.proposalService.open({
          idBusinessPartner: params.get('idBusinessPartner') ?? undefined,
          idCarrello: params.get('idCarrello') ?? undefined,
        });

    open$.subscribe(proposal => this.onOpened(proposal, !id));
  }

  ngOnDestroy(): void {
    this.headerService.removeTitle();
    this.headerService.removeActions();
  }

  openSelectCustomer(): void {
    void this.router.navigate(['/new-order-proposal/select-customer'], { queryParams: { id: this.proposal()?.id } });
  }

  openSelectProducts(): void {
    void this.router.navigate(['/new-order-proposal/select-products'], { queryParams: { id: this.proposal()?.id } });
  }

  continueToShipping(): void {
    void this.router.navigate(['/new-order-proposal/shipping-payment'], { queryParams: { id: this.proposal()?.id } });
  }

  canContinue(): boolean {
    return this.customerSelected() && this.productsSelected();
  }

  private onOpened(proposal: OrderProposal, created: boolean): void {
    // Ordine già inviato: si apre il dettaglio, non la proposta
    if (!isDraftProposal(proposal)) {
      void this.router.navigate(['/order-detail'], { queryParams: { id: proposal.id }, replaceUrl: true });
      return;
    }

    // L'id resta nell'indirizzo: ricaricando la pagina si riapre la stessa proposta
    if (created) {
      void this.router.navigate([], { queryParams: { id: proposal.id }, replaceUrl: true });
    }

    this.cartService.getCart(proposal.carrello.id).pipe(finalize(() => this.ready.set(true))).subscribe();

    // Proposta nuova con il cliente già impostato: i codici da scegliere si chiedono subito
    if (created && proposalNeedsCodeChoice(proposal)) {
      this.proposalService.chooseCustomerCodes().subscribe();
    }
  }

  // Uscita da una proposta mai salvata: bozza con nome facoltativo oppure niente
  private close(): void {
    this.dialog.open<SaveDraftDialog, unknown, SaveDraftDialogResult>(SaveDraftDialog, { width: '400px', disableClose: true })
      .afterClosed()
      .subscribe(result => {
        if (result?.action === 'save') {
          this.proposalService.saveDraft(result.name).pipe(
            switchMap(saved => this.dialog.open(DraftSavedDialog, {
              width: '400px',
              disableClose: true,
              data: { name: saved.descrizione },
            }).afterClosed()),
          ).subscribe(goToDrafts => {
            if (goToDrafts) {
              this.goToDrafts();
            }
          });
        } else if (result?.action === 'exit') {
          this.proposalService.exitWithoutSaving().subscribe(() => this.goToDrafts());
        }
      });
  }

  private goToDrafts(): void {
    void this.router.navigate(['/orders/list'], { queryParams: { view: 'drafts' }, replaceUrl: true });
  }
}
